import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { NotFoundView } from '@order/components';
import { useFeatureFlag } from '@order/LaunchDarkly';

import { NotificationPreferences } from './components';
import { SUBSCRIPTION_TYPE } from './constants';
import { useNotificationPreferences } from './hooks';
import {
  type NotificationCategory,
  NotificationCategoryType,
} from './hooks/types';
import { NotificationPreferencesLoading } from './NotificationPreferencesLoading';
import {
  CATEGORY_MESSAGES,
  messages,
} from './NotificationPreferencesScreen.messages';

// ─────────────────────────────────────────────────────────────────────

export const NotificationPreferencesScreen = () => {
  const { formatMessage } = useIntl();

  const {
    preferences,
    isUpdating,
    permissions,
    isLoading,
    error,
    updateNotificationPreferences,
    refetch,
  } = useNotificationPreferences();

  const screenTitle = formatMessage(messages.title);

  // ─── Flags ───────────────────────────────────────────────────────────

  const isOrderStatusEmailNotificationEnabled = useFeatureFlag(
    'CELS-3514-allow-order-status-email-preferences',
  );
  const isNotificationPreferencesEnabled = useFeatureFlag(
    'CELS-3674-enable-notification-preference-center',
  );

  // ─── Helpers ─────────────────────────────────────────────────────────

  const getCategoryTitle = useCallback(
    (category: NotificationCategory) => {
      const translationKey = category.id as NotificationCategoryType;

      return CATEGORY_MESSAGES[translationKey]?.title
        ? formatMessage(CATEGORY_MESSAGES[translationKey].title)
        : category.displayName;
    },
    [formatMessage],
  );

  const getCategorySubtitle = useCallback(
    (categoryId: string) => {
      const subtitleKey = categoryId as NotificationCategoryType;

      return CATEGORY_MESSAGES[subtitleKey]?.subtitle
        ? formatMessage(CATEGORY_MESSAGES[subtitleKey].subtitle)
        : null;
    },
    [formatMessage],
  );

  // ─────────────────────────────────────────────────────────────────────

  if (!isNotificationPreferencesEnabled) return <NotFoundView />;

  if (isLoading) return <NotificationPreferencesLoading />;

  if (error || !preferences?.length) {
    return (
      <NotificationPreferences.Container title={screenTitle}>
        <NotFoundView
          withoutFooter
          btnLabel={formatMessage(messages.updateErrorButtonText)}
          onBtnPress={refetch}
        />
      </NotificationPreferences.Container>
    );
  }

  // ─────────────────────────────────────────────────────────────────

  return (
    <NotificationPreferences.Container title={screenTitle}>
      {permissions.isGranted ? null : (
        <NotificationPreferences.Notice
          notice={formatMessage(messages.pushDisabledNoticeTitle)}
          actionText={formatMessage(messages.pushDisabledNoticeActionTitle)}
          onPress={permissions.openSettings}
        />
      )}

      {preferences.map((preference) => {
        const title = getCategoryTitle(preference);
        const subtitle = getCategorySubtitle(preference.id);

        const isOrderStatusOption =
          preference.id === NotificationCategoryType.OrderStatus;

        const shouldRenderOrderStatusEmailOption =
          !isOrderStatusOption ||
          (isOrderStatusOption && isOrderStatusEmailNotificationEnabled);

        return (
          <NotificationPreferences.Content key={preference.id}>
            {title ? (
              <NotificationPreferences.Title>
                {title}
              </NotificationPreferences.Title>
            ) : null}

            {subtitle ? (
              <NotificationPreferences.Subtitle>
                {subtitle}
              </NotificationPreferences.Subtitle>
            ) : null}

            <NotificationPreferences.Control.Container>
              <NotificationPreferences.Control.Section>
                <NotificationPreferences.Control.Title>
                  {formatMessage(messages.pushNotificationsText)}
                </NotificationPreferences.Control.Title>

                <NotificationPreferences.Control.Toggle
                  value={preference.pushSubscribe}
                  subscriptionType={SUBSCRIPTION_TYPE.PUSH}
                  category={preference.id}
                  onChange={updateNotificationPreferences}
                  disabled={isUpdating}
                />
              </NotificationPreferences.Control.Section>

              {shouldRenderOrderStatusEmailOption ? (
                <NotificationPreferences.Control.Section>
                  <NotificationPreferences.Control.Title>
                    {formatMessage(messages.emailNotificationText)}
                  </NotificationPreferences.Control.Title>

                  <NotificationPreferences.Control.Toggle
                    value={preference.emailSubscribe}
                    subscriptionType={SUBSCRIPTION_TYPE.EMAIL}
                    category={preference.id}
                    onChange={updateNotificationPreferences}
                    disabled={isUpdating}
                  />
                </NotificationPreferences.Control.Section>
              ) : null}
            </NotificationPreferences.Control.Container>
          </NotificationPreferences.Content>
        );
      })}
    </NotificationPreferences.Container>
  );
};
